import { React, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getClassesBySchoolId,
  getClassAssessmentsForMultipleClasses,
} from "../services/APIClient";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SencoResultsClassOverview from "../components/SencoResultsClassOverview";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import "../components/ClassOverview.css";

const YEAR_RANGES = {
  "24/25": { start: "2024-08-15", end: "2025-08-15" },
  "23/24": { start: "2023-08-15", end: "2024-08-15" },
  "22/23": { start: "2022-08-15", end: "2023-08-15" },
};

function SencoResultDetail(props) {
  const location = useLocation();
  let schoolid = location?.state?.schoolid;
  let teacherid = location?.state?.teacherid;
  let name = location.state.name.name;
  let teachers = location.state.teachers.teachers;
  let gradeLevel = location.state?.gradeLevel?.gradeLevel;

  const [classes, setClasses] = useState([]);
  const [classIds, setClassIds] = useState([]);
  const [allClassesAssessments, setAllClassesAssessments] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null); // Default year
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)")?.matches
  );
  const [isLoading, setIsLoading] = useState(false); // Add loading state

  const BATCH_SIZE = 5;

  function fetchAssessmentsInBatches(classIds) {
    const batches = [];
    for (let i = 0; i < classIds.length; i += BATCH_SIZE) {
      batches.push(classIds.slice(i, i + BATCH_SIZE));
    }

    const fetchPromises = batches.map((batch) => {
      return getClassAssessmentsForMultipleClasses(batch);
    });

    return Promise.all(fetchPromises).then((responses) => {
      return responses.flat();
    });
  }

  useEffect(() => {
    if (!schoolid || !selectedYear) return;

    setIsLoading(true); // Set loading to true when starting to fetch

    const { start, end } = YEAR_RANGES[selectedYear];

    getClassesBySchoolId(schoolid, { start, end })
      .then((response) => {
        setClasses(response);
        const ids = response?.map((classItem) => classItem.id);
        setClassIds(ids);
      })
      .catch((err) => {
        console.error("Error fetching classes by school ID:", err);
        setIsLoading(false); // Set loading to false if there's an error
      });
  }, [schoolid, selectedYear]);

  useEffect(() => {
    if (classIds?.length > 0) {
      setIsLoading(true); // Set loading to true when fetching assessments

      fetchAssessmentsInBatches(classIds)
        .then((response) => {
          setAllClassesAssessments(response);
          setIsLoading(false); // Set loading to false when done
        })
        .catch((error) => {
          console.error("Error fetching assessments:", error);
          setIsLoading(false); // Set loading to false if there's an error
        });
    }
  }, [classIds]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window?.innerWidth < window?.innerHeight);
    };

    window?.addEventListener("resize", handleOrientationChange);

    return () => {
      window?.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    window?.HubSpotConversations?.widget?.refresh();
  }, []);

  const gradeLevel1Assessments = [];
  const gradeLevel2Assessments = [];

  allClassesAssessments?.forEach((classAssessment) => {
    // Find the latest assessment date for this class
    let latestAssessmentDate = null;

    Object.values(classAssessment.assessments).forEach(assessment => {
      const assessmentDate = new Date(assessment?.firstAssignedDate);
      if (!latestAssessmentDate || assessmentDate > latestAssessmentDate) {
        latestAssessmentDate = assessmentDate;
      }
    });

    // Only process this class if its latest assessment falls in the selected year range
    if (!selectedYear || !YEAR_RANGES[selectedYear] || !latestAssessmentDate) return;

    const { start, end } = YEAR_RANGES[selectedYear];
    const startDate = new Date(start);
    const endDate = new Date(end);

    // Check if the latest assessment date falls within the selected year range
    if (latestAssessmentDate >= startDate && latestAssessmentDate <= endDate) {
      const gradeLevel1Data = {
        classId: classAssessment?.classId,
        assessments: {},
      };
      const gradeLevel2Data = {
        classId: classAssessment?.classId,
        assessments: {},
      };

      // If class belongs to this year, include ALL its assessments (not date-filtered)
      Object.entries(classAssessment.assessments).forEach(
        ([assessmentId, assessment]) => {
          if (assessment?.grade_level1) {
            gradeLevel1Data.assessments[assessmentId] = assessment;
          }
          if (assessment?.grade_level2) {
            gradeLevel2Data.assessments[assessmentId] = assessment;
          }
        }
      );

      if (Object.keys(gradeLevel1Data.assessments).length > 0) {
        gradeLevel1Assessments.push(gradeLevel1Data);
      }

      if (Object.keys(gradeLevel2Data.assessments).length > 0) {
        gradeLevel2Assessments.push(gradeLevel2Data);
      }
    }
  });

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={name}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
                <p>Loading data, please wait...</p>
              </div>
            </div>
          ) : !selectedYear ? (
            <Card className="dynamic-box">
              <div className="school-info-header mb-3">
                <h4 className="mb-1">{name}</h4>
              </div>
              <h5 className="step-indicator">Step 1: Select Academic Year</h5>
              <Row className="justify-content-center mb-3">
                {Object.keys(YEAR_RANGES).map((year) => (
                  <Col md={4} key={year} className="mb-3">
                    <button
                      className={`year-selection-button w-100 py-3 shadow-sm rounded ${selectedYear === year ? "selected" : ""
                        }`}
                      onClick={() => setSelectedYear(year)}
                    >
                      <h3>{year}</h3>
                      <small className="text-muted d-block mt-2">
                        {new Date(YEAR_RANGES[year].start).toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric" }
                        )}{" "}
                        -
                        {new Date(YEAR_RANGES[year].end).toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric" }
                        )}
                      </small>
                    </button>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : (
            <>
              <div className="current-selections p-2 mb-3 bg-light rounded mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{name}</h5>
                    <p className="mb-0 text-muted">
                      Academic Year: {selectedYear}
                    </p>
                  </div>
                  <button
                    variant="secondary"
                    className="blue-action-button"
                    onClick={() => setSelectedYear(null)}
                  >
                    Change Year
                  </button>
                </div>
              </div>
              <SencoResultsClassOverview
                teachers={teachers}
                gradeLevel={gradeLevel}
                name={name}
                teacherid={teacherid}
                schoolid={schoolid}
                allClassesAssessments={allClassesAssessments}
                classIds={classIds}
                gradeLevel1Assessments={gradeLevel1Assessments}
                gradeLevel2Assessments={gradeLevel2Assessments}
                selectedYear={selectedYear}
                YEAR_RANGES={YEAR_RANGES}
              />
            </>
          )}
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default SencoResultDetail;